// Landing page from the portal-invite email. The action_link Supabase
// minted carries an invite token in the URL hash; supabase-js's
// onAuthStateChange picks it up and emits a PASSWORD_RECOVERY event,
// after which we can call updateUser to set the password.
//
// The page is mounted at /portal/set-password — matches PORTAL_BASE_URL
// configured in the portal-invite function's env.

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Zap, Lock, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'

export default function PortalSetPassword() {
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  // Track whether Supabase has surfaced an auth session from the URL
  // token. While waiting, render a small "validating link" state.
  const [tokenAccepted, setTokenAccepted] = useState(false)
  // SMS consent — OPTIONAL, unchecked by default (affirmative opt-in only).
  const [smsConsent, setSmsConsent] = useState(false)
  const [businessName, setBusinessName] = useState<string | null>(null)

  // The business name for the consent text comes from the workspace (portal_workspace
  // view, same source PortalLayout uses). Fetched once the invite session is live.
  useEffect(() => {
    if (!tokenAccepted) return
    supabase.from('portal_workspace').select('name').maybeSingle()
      .then(({ data }) => setBusinessName((data?.name as string | undefined) ?? null))
  }, [tokenAccepted])

  // The exact consent language shown AND recorded (one string → snapshot matches
  // display verbatim). Business name resolved; safe fallback if it hasn't loaded.
  const consentText = `I agree to receive SMS text messages from ${businessName ?? 'our team'} about my service requests, appointments, and account — including appointment reminders, technician updates, and responses to my inquiries. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for help. Consent is not a condition of purchase.`

  useEffect(() => {
    // PASSWORD_RECOVERY / SIGNED_IN events fire after supabase-js parses
    // the URL hash. Either one means we have a session and can call
    // updateUser. Subscribe + also do a one-shot getSession check in
    // case we hit the page after the event already fired.
    let resolved = false
    const sub = supabase.auth.onAuthStateChange((event, session) => {
      if (!resolved && session?.user && (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN' || event === 'USER_UPDATED')) {
        resolved = true
        setTokenAccepted(true)
      }
    })
    supabase.auth.getSession().then(({ data }) => {
      if (!resolved && data.session?.user) { resolved = true; setTokenAccepted(true) }
    })
    return () => { sub.data.subscription.unsubscribe() }
  }, [])

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return
    if (password.length < 8) { setErr('Password must be at least 8 characters'); return }
    if (password !== confirm) { setErr('Passwords don\'t match'); return }
    setBusy(true); setErr(null)
    const { error } = await supabase.auth.updateUser({ password })
    if (error) {
      setBusy(false)
      setErr(error.message || 'Failed to set password')
      return
    }
    // Onboarding has succeeded. If the customer affirmatively opted in, record SMS
    // consent via the service-role edge fn (portal users can't update contacts under
    // RLS). Best-effort: a failure must NOT block onboarding — surface it, move on.
    if (smsConsent) {
      const { error: consentErr } = await supabase.functions.invoke('portal-record-consent', {
        body: { consented: true, language: consentText },
      })
      if (consentErr) {
        // Best-effort: onboarding still completes. Surface a non-blocking toast so a
        // dropped consent isn't silent (e.g. the fn isn't deployed on this project).
        console.warn('[portal] SMS consent record failed', consentErr.message)
        toast.error("We couldn't save your SMS preference — you can update it later.")
      }
    }
    setDone(true)
    // Brief pause for the success state, then route into the portal.
    setTimeout(() => navigate('/portal', { replace: true }), 800)
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--canvas)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 380 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 28 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: 'var(--logo-bg-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Zap style={{ width: 18, height: 18, color: 'var(--accent-ink)' }} />
          </div>
          <p style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0, letterSpacing: '-0.02em' }}>
            Client Portal
          </p>
        </div>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: 24 }}>
          {!tokenAccepted ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--ink-secondary)' }}>
              <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
              Validating invite link…
            </div>
          ) : done ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: 'var(--positive)' }}>
              <CheckCircle2 style={{ width: 16, height: 16 }} />
              Password set. Signing you in…
            </div>
          ) : (
            <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <h1 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: '0 0 4px', letterSpacing: '-0.02em' }}>Set your password</h1>
              <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: 0 }}>
                Pick a password — at least 8 characters. You'll use it to sign in next time.
              </p>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>New password</label>
                <div style={{ position: 'relative' }}>
                  <Lock style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: 'var(--ink-tertiary)' }} />
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)} required autoFocus
                    style={{ width: '100%', padding: '10px 12px 10px 33px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 14, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Confirm</label>
                <div style={{ position: 'relative' }}>
                  <Lock style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: 'var(--ink-tertiary)' }} />
                  <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required
                    style={{ width: '100%', padding: '10px 12px 10px 33px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 14, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }} />
                </div>
              </div>
              {err && (
                <div role="alert" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px', background: 'var(--danger-dim)', border: '1px solid var(--danger-border)', borderRadius: 7 }}>
                  <AlertCircle style={{ width: 13, height: 13, color: 'var(--danger)', flexShrink: 0, marginTop: 2 }} />
                  <span style={{ fontSize: 12, color: 'var(--danger)' }}>{err}</span>
                </div>
              )}
              {/* SMS consent — OPTIONAL, unchecked by default. Onboarding completes
                  with or without it (consent is not a condition of purchase). */}
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, cursor: 'pointer', padding: '2px 0' }}>
                <input type="checkbox" checked={smsConsent} onChange={e => setSmsConsent(e.target.checked)}
                  style={{ width: 15, height: 15, marginTop: 1, flexShrink: 0, accentColor: 'var(--accent)', cursor: 'pointer' }} />
                <span style={{ fontSize: 11, lineHeight: 1.5, color: 'var(--ink-tertiary)' }}>{consentText}</span>
              </label>
              <button type="submit" disabled={busy}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '11px', background: 'var(--accent)', border: 'none', borderRadius: 9, color: 'var(--accent-ink)', fontSize: 14, fontWeight: 700, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1 }}>
                {busy && <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />}
                {busy ? 'Saving…' : 'Save and continue'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
